<template>
  <div
    class="custom-menu"
    :class="{
      'custom-menu--root': isRoot,
      'custom-menu--collapsed': isCollapsedView,
      'custom-menu--with-margin': hasMargin
    }"
  >
    <div
      v-for="menu in visibleRoutes"
      :key="getNodeKey(menu)"
      class="menu-node"
      :class="{
        'menu-node--active': hasActiveDescendant(menu),
        'menu-node--open': isOpen(menu)
      }"
    >
      <button
        type="button"
        class="menu-item"
        :class="{ 'menu-item--leaf': !hasChildren(menu) }"
        :id="!hasChildren(menu) ? menu.id : undefined"
        @click="onNodeClick(menu)"
      >
        <span class="menu-item__content">
          <a-icon class="menu-item__icon" width="16" height="16" :icon="menu.icon" />
          <span class="menu-item__label" :class="{ 'menu-item__label--hidden': isCollapsedView }">{{
            menu.title
          }}</span>
        </span>
        <a-icon
          v-if="hasChildren(menu)"
          class="menu-item__chevron"
          :class="{
            'menu-item__chevron--hidden': isCollapsedView,
            'menu-item__chevron--open': isOpen(menu)
          }"
          width="14"
          height="14"
          icon="mingcute:right-line"
        />
      </button>

      <div v-if="hasChildren(menu) && !isCollapsedView && isOpen(menu)" class="submenu-inline">
        <recursive-menu
          :visibleRoutes="menu.nestedRoutes"
          :selectedKeys="selectedKeys"
          :tourIsOn="tourIsOn"
          :hasMargin="false"
          :collapsed="false"
          :isRoot="false"
        />
      </div>

      <div v-if="hasChildren(menu) && isCollapsedView" class="submenu-popover">
        <div class="submenu-popover__header">{{ menu.title }}</div>
        <recursive-menu
          :visibleRoutes="menu.nestedRoutes"
          :selectedKeys="selectedKeys"
          :tourIsOn="tourIsOn"
          :hasMargin="false"
          :collapsed="false"
          :isRoot="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Modal, notification } from 'ant-design-vue'

export default {
  name: 'RecursiveMenu',
  components: {
  },
  props: {
    visibleRoutes: { type: Array, required: true },
    selectedKeys: { type: Array, default: () => [] },
    tourIsOn: { type: Boolean, default: false },
    hasMargin: { type: Boolean, default: true },
    collapsed: { type: Boolean, default: false },
    isRoot: { type: Boolean, default: false }
  },
  data() {
    return {
      notImplementedRoutes: ['setup-company'],
      openKeys: []
    }
  },
  computed: {
    currentRoute() {
      return this.$route.name || ''
    },
    isCollapsedView() {
      return this.collapsed && this.isRoot
    }
  },
  watch: {
    currentRoute: {
      handler(newVal) {
        if (this.isCollapsedView) {
          this.openKeys = []
          return
        }

        const activeRoute = this.findTopParentKeyByCurrentPath(this.visibleRoutes, newVal)

        if (activeRoute) {
          this.openKeys = Array.from(new Set([...this.openKeys, activeRoute]))
        }
      },
      immediate: true
    },
    isCollapsedView(newVal) {
      if (newVal) {
        this.openKeys = []
      }
    }
  },
  methods: {
    getNodeKey(menu) {
      return menu.id || menu.key || menu.title
    },
    hasChildren(menu) {
      return Array.isArray(menu.nestedRoutes) && menu.nestedRoutes.length > 0
    },
    isOpen(menu) {
      return this.openKeys.includes(this.getNodeKey(menu))
    },
    toggleOpen(menu) {
      const key = this.getNodeKey(menu)
      if (this.isOpen(menu)) {
        this.openKeys = this.openKeys.filter(item => item !== key)
      } else {
        this.openKeys = [...this.openKeys, key]
      }
    },
    findTopParentKeyByCurrentPath(routes, currentKey) {
      function search(route, topParentId = null) {
        if (route.key === currentKey) {
          return topParentId
        }

        if (route.nestedRoutes && Array.isArray(route.nestedRoutes)) {
          const currentTopId = route.id ? route.id : topParentId

          for (const child of route.nestedRoutes) {
            const result = search(child, currentTopId)
            if (result) return result
          }
        }

        return null
      }

      for (const route of routes) {
        if (route.key === currentKey) {
          return route.id || route.key
        }

        const result = search(route, route.id || null)
        if (result) return result
      }

      return null
    },
    hasActiveDescendant(route) {
      if (!route) return false
      if (route.key === this.currentRoute || this.currentRoute.startsWith(`${route.key}.`)) return true
      if (!Array.isArray(route.nestedRoutes)) return false

      return route.nestedRoutes.some(child => this.hasActiveDescendant(child))
    },
    onNodeClick(menu) {
      if (this.hasChildren(menu)) {
        if (!this.isCollapsedView) {
          this.toggleOpen(menu)
        }
        return
      }

      if (menu.onClick) {
        menu.onClick()
        return
      }

      this.handleClick({ key: menu.key, title: menu.title })
    },
    handleClick(e) {
      Modal.destroyAll()

      if (this.notImplementedRoutes.includes(e.key)) {
        notification.info({
          message: `${e.title} Not Ready Yet!`,
          description: `We're still developing the ${e.title} feature for you. Once it's ready, you'll be able to see them right here!`
        })

        return
      }

      if (!e.key || e.key === this.currentRoute) {
        return
      }

      const routeKey = e.key === 'home' ? '' : e.key
      this.$router.push('/' + routeKey).catch(() => {})
    }
  }
}
</script>

<style scoped>
.custom-menu {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg-component);
  color: var(--text-color);
  overflow-y: auto;
}

.custom-menu--with-margin {
  transform: translateX(30px);
  width: calc(100% - 30px);
  border-left: 1px solid #999999;
}

.menu-node {
  position: relative;
}

.menu-item {
  width: calc(100% - 16px);
  margin: 4px 8px;
  padding: 0 16px;
  min-height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    padding 0.25s ease;
}

.menu-item:hover {
  background: var(--bg-dropdown-selected);
  color: var(--primary-color);
}

.menu-item__content {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  transition: gap 0.25s ease;
}

.menu-item__icon {
  min-width: 16px;
}

.menu-item__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  max-width: 220px;
  opacity: 1;
  transform: translateX(0);
  transition:
    opacity 0.2s ease,
    max-width 0.25s ease,
    transform 0.25s ease;
}

.menu-item__label--hidden {
  display: none;
  transform: translateX(-4px);
}

.menu-item__chevron {
  opacity: 0.65;
  max-width: 18px;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-width 0.2s ease;
}

.menu-item__chevron--open {
  transform: rotate(90deg);
}

.menu-item__chevron--hidden {
  display: none;
  max-width: 0;
}

.menu-node--active > .menu-item {
  background: var(--bg-dropdown-selected);
  color: var(--primary-color);
}

.submenu-inline {
  padding: 0 0 0 14px;
  animation: submenu-expand 0.2s ease;
}

.custom-menu--collapsed {
  overflow: visible;
}

.custom-menu--collapsed .menu-item {
  width: calc(100% - 16px);
  padding: 8px 8px;
  justify-content: center;
}

.custom-menu--collapsed .menu-item__label,
.custom-menu--collapsed .menu-item__chevron {
  pointer-events: none;
}

.submenu-popover {
  position: absolute;
  left: 100%;
  top: -8px;
  z-index: 50;
  width: 280px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--border-color-base);
  border-radius: 12px;
  background: var(--bg-card);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
  padding: 10px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(6px);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    visibility 0.18s ease;
}

.submenu-popover__header {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color-secondary);
  margin: 2px 8px 10px;
}

.custom-menu--collapsed .menu-node::after {
  content: '';
  position: absolute;
  right: -12px;
  top: 0;
  width: 16px;
  height: 100%;
}

.custom-menu--collapsed .menu-node:hover > .submenu-popover {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(0);
}

.submenu-popover .custom-menu {
  overflow-x: hidden !important;
}

.submenu-popover .custom-menu .menu-item {
  justify-content: flex-start;
}

@keyframes submenu-expand {
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes submenu-fade-in {
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
